import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import moment from 'moment-timezone'
import { BusinessAccountFinanceFeaturesContext } from '@Admin/BusinessAccountDetailsPage/context'
import { useSelector } from 'react-redux'
import { message, Switch } from 'antd'

import Box from '~/core/Box'
import Text from '~/core/Text'
import Tooltip from '~/core/Tooltip'
import logger from '~/lib/logger'
import { getUserIdentity } from '~/redux-modules/identity'
import {
    BusinessAccountFeatureInput,
    GetAllBusinessAccountFeaturesDocument,
    useUpdateBusinessAccountFeatureMutation,
} from '~/gql/graphql'

interface AdminAccountSettingsSwitchProps {
    enabled?: boolean
    tooltipTitle: string
    financeFeature: BusinessAccountFeatureInput
    requireFinanceRole?: boolean
    'data-test-target'?: string
}

export default function AdminAccountSettingsSwitch({
    enabled,
    tooltipTitle,
    financeFeature,
    requireFinanceRole = true,
    'data-test-target': dataTestTarget,
}: AdminAccountSettingsSwitchProps) {
    const [disabled, setDisabled] = useState(true)
    const { isBusinessAdmin } = useSelector(getUserIdentity)
    const { businessAccountId } = useParams<{ businessAccountId: string }>()
    const { hasAssignFinanceCapabilities, businessAccountFeatures } =
        useContext(BusinessAccountFinanceFeaturesContext)
    const [updateBusinessAccountFeature, { loading }] =
        useUpdateBusinessAccountFeatureMutation()

    const businessAccountFeature = businessAccountFeatures.find(
        feature => feature.featureName === financeFeature,
    )

    const handleSwitch = async () => {
        const variables = {
            businessAccountId,
            featureName: financeFeature,
            isEnabled: !enabled,
        }

        const failureMessage = requireFinanceRole
            ? 'Failed to updated business account finance settings'
            : 'Failed to update business account settings'

        try {
            await updateBusinessAccountFeature({
                variables,
                refetchQueries: [GetAllBusinessAccountFeaturesDocument],
            })
            message.success('Business account settings updated')
        } catch (err) {
            message.error(failureMessage)
            logger.exception(err, {
                extra: {
                    info: failureMessage,
                    variables,
                },
            })
        }
    }

    let enableSwitch = (
        <Switch
            checked={enabled}
            disabled={!disabled || loading}
            onClick={() => handleSwitch()}
            style={{ marginRight: '12px' }}
            data-test-target={dataTestTarget}
        />
    )

    if (
        !isBusinessAdmin &&
        requireFinanceRole &&
        !hasAssignFinanceCapabilities
    ) {
        enableSwitch = (
            <Tooltip title={tooltipTitle} placement="bottomLeft">
                {enableSwitch}
            </Tooltip>
        )
    }

    useEffect(() => {
        setDisabled(
            isBusinessAdmin &&
                (!requireFinanceRole || hasAssignFinanceCapabilities),
        )
    }, [hasAssignFinanceCapabilities, isBusinessAdmin, requireFinanceRole])

    let enabledDisabledText = <Text>{enabled ? 'Enabled' : 'Disabled'}</Text>

    if (businessAccountFeature?.updatedAt) {
        enabledDisabledText = (
            <Text>{`${enabled ? 'Enabled' : 'Disabled'} on ${moment(
                businessAccountFeature?.updatedAt,
            ).format('LL')}`}</Text>
        )
    }

    return (
        <Box alignItems="center">
            {enableSwitch}
            {enabledDisabledText}
        </Box>
    )
}
